const h1 = (text) => ({ type: "h1", text });
const h2 = (text) => ({ type: "h2", text });
const p = (text) => ({ type: "p", text });
const ul = (items) => ({ type: "ul", items });
const ol = (items) => ({ type: "ol", items });
const img = (stem, alt) => ({ type: "image", stem, alt });
const note = (text) => ({ type: "note", text });
const callout = (title, text, items) => ({ type: "callout", title, text, items });
const rich = (before, href, label, after) => ({ type: "rich", before, href, label, after });

const PATHS = {
  en: "/aviatortrends-guide",
  es: "/es/guia-aviatortrends",
  "pt-BR": "/pt-br/guia-aviatortrends",
  "pt-PT": "/pt-pt/guia-aviatortrends",
  de: "/de/aviatortrends-ratgeber",
  it: "/it/guida-aviatortrends",
};

const copy = {
  en: {
    title: "Master Spribe Aviator & Crash Games with AviatorTrends",
    description: "Learn how to analyze Aviator game data, detect happy hours, and forecast crash multipliers with AviatorTrends. Boost your game strategy now.",
    blocks: [
      h1("Master Spribe Aviator and Crash Games with AviatorTrends"),
      p("Whether you’re a casual flier or chasing that big multiplier, AviatorTrends gives you the data-driven edge. In this guide you’ll learn how to:"),
      ul([
        "Download or analyze Aviator round history results data",
        "Use Round History for happy hour hunting",
        "Spot patterns with Time Series graphs",
        "Predict smarter with the Histogram and Forecast",
        "Combine all three tools into a winning strategy",
      ]),
      h2("Download or Analyze Round History Results Data"),
      p("Get the last 300+ rounds for statistically reliable trends. Download from AviatorTrends as Excel CSV or analyze directly in-browser with filters."),
      note("Pro Tip: Always use 300+ datapoints to ensure your Probability Distribution and Forecast are meaningful."),
      img("spribe-aviator-graphical-analysis", "300 round data sample"),
      h2("1. Round History: Your Happy Hour Detector"),
      p("The Round History panel streams live multipliers as they occur. Look for clusters of 10×+ multipliers across multiple casinos in a short span."),
      ul([
        "Filter by casino and set threshold (e.g., ≥10×) to catch simultaneous bursts.",
        "Shift strategy to moderate/aggressive when happy hour forms.",
      ]),
      img("aviator-round-history", "Happy hour detector"),
      h2("2. Time Series: Spot the Trend"),
      p("Plot crash multipliers over recent rounds. Identify volatility zones, cycles, and build-up patterns."),
      ul([
        "Zoom in on 300 rounds for short-term analysis.",
        "Use 500+ to detect longer cycles.",
        "Look for recurring highs every 30–60 rounds.",
      ]),
      p("In this example, 100 datapoints show no clear pattern:"),
      img("aviator-trends-analysis-timeseries-100", "aviator trends analysis timeseries 100"),
      p("But with 300 datapoints, a pattern of spikes appears around 13:55, 14:35, and 15:00. Averagely every 30–40 mins, so expect one around 15:30–15:40."),
      img("aviator-trends-analysis-timeseries-300", "aviator trends analysis timeseries 300"),
      p("At 15:41, the multiplier hit 337.28× — spot on!"),
      img("spribe-aviator-prediction", "Spribe Aviator Prediction"),
      h2("3. Histogram (Probability Distribution): Your Prediction Engine"),
      p("This shows how often multipliers fall in each bin. Forecast column tells you which ranges are overdue."),
      img("forecast", "Forecast distribution"),
      p("Green (positive forecast) → “due” bins. Red (negative) → likely cooldowns. Looks like there's a happy hour coming up! Track 300+ datapoints for accuracy."),
      h2("4. Bringing It All Together: A Winning Playbook"),
      ol([
        "Analyze or download Aviator round history",
        "Check Round History for happy hours",
        "Use Time Series for trend cycles",
        "Forecast best multiplier targets via Histogram",
        "Log outcomes and refine strategy",
      ]),
      h2("Example Strategies"),
      ul([
        "Conservative: 1.3×–1.5× cashout when green",
        "Moderate: 5×–6× when 5.0–10.0 shows +3%",
        "Aggressive: 20×–25× when 10–100 bins are hot",
      ]),
      h2("Bankroll Management"),
      ul([
        "Set a strict budget per session (e.g., $50)",
        "Avoid chasing losses",
        "Only use Martingale-style systems for happy hours",
      ]),
      callout(
        "Responsible Gambling Reminder",
        "The Aviator crash game is powered by RNG. AviatorTrends helps you analyze—but cannot guarantee wins. Play responsibly:",
        ["Set loss limits", "Only bet what you can afford", "Treat the game as entertainment"]
      ),
      h2("Ready to Level Up?"),
      rich("Head over to ", "https://aviatortrends.com", "AviatorTrends", " now and start predicting crash multipliers like a pro."),
    ],
  },
  es: {
    title: "Analiza Aviator de Spribe y los juegos crash con AviatorTrends",
    description: "Aprende a revisar el historial de Aviator, observar rachas de multiplicadores altos y leer las gráficas de AviatorTrends. El juego usa RNG y no hay ganancias garantizadas.",
    blocks: [
      h1("Analiza Aviator de Spribe y los juegos crash con AviatorTrends"),
      p("AviatorTrends organiza el historial y las gráficas de Aviator para que puedas revisar los datos antes de decidir. En esta guía verás cómo:"),
      ul([
        "Descargar o analizar el historial de rondas de Aviator",
        "Usar el historial de rondas para observar rachas altas",
        "Buscar variaciones en las gráficas de serie temporal",
        "Leer el histograma y la columna de previsión",
        "Combinar las tres herramientas en una rutina de análisis",
      ]),
      h2("Descarga o analiza el historial de rondas"),
      p("Reúne las últimas 300 rondas o más para que la tendencia tenga más contexto. Puedes descargar un CSV de Excel desde AviatorTrends o aplicar filtros directamente en el navegador."),
      note("Consejo: usa 300 puntos o más para que la distribución de probabilidad y la previsión tengan suficiente contexto."),
      img("spribe-aviator-graphical-analysis", "Muestra de 300 rondas"),
      h2("1. Historial de rondas: detector de rachas altas"),
      p("El panel de historial muestra los multiplicadores en vivo. Busca grupos de resultados de 10× o más en varios casinos durante un intervalo corto."),
      ul([
        "Filtra por casino y define un umbral, por ejemplo ≥10×, para ver ráfagas simultáneas.",
        "Si aparece una racha alta, revisa de nuevo la serie y el histograma antes de cambiar el ritmo.",
      ]),
      img("aviator-round-history", "Detector de rachas altas"),
      h2("2. Serie temporal: observa la variación"),
      p("La serie temporal muestra los multiplicadores de las rondas recientes. Sirve para ver zonas de volatilidad, ciclos y periodos de acumulación."),
      ul([
        "Amplía 300 rondas para una revisión corta.",
        "Usa 500 o más para buscar ciclos más largos.",
        "Observa si los picos se repiten cada 30–60 rondas.",
      ]),
      p("En este ejemplo, 100 puntos no muestran un patrón claro:"),
      img("aviator-trends-analysis-timeseries-100", "Serie temporal de Aviator con 100 puntos"),
      p("Con 300 puntos, el ejemplo muestra picos alrededor de las 13:55, 14:35 y 15:00. El intervalo aproximado es de 30–40 minutos; en ese caso el siguiente se esperaba cerca de las 15:30–15:40."),
      img("aviator-trends-analysis-timeseries-300", "Serie temporal de Aviator con 300 puntos"),
      p("A las 15:41, en este ejemplo, el multiplicador llegó a 337,28×. Un resultado pasado no determina la ronda siguiente."),
      img("spribe-aviator-prediction", "Ejemplo de revisión de Aviator de Spribe"),
      h2("3. Histograma: distribución de probabilidad"),
      p("El histograma cuenta cuántas veces cae el multiplicador en cada intervalo. La columna de previsión señala intervalos que aparecen menos de lo habitual en la muestra."),
      img("forecast", "Distribución de la previsión"),
      p("Verde, previsión positiva, marca intervalos menos frecuentes en la muestra. Rojo, previsión negativa, marca intervalos que acaban de aparecer. Revisa 300 puntos o más antes de interpretarlo."),
      h2("4. Cómo combinar las tres herramientas"),
      ol([
        "Analiza o descarga el historial de rondas",
        "Revisa el historial para buscar rachas altas",
        "Usa la serie temporal para ver ciclos",
        "Consulta el histograma para comparar intervalos",
        "Registra los resultados y ajusta la revisión",
      ]),
      h2("Ejemplos de lectura"),
      ul([
        "Conservador: revisar cobros de 1,3×–1,5× cuando el intervalo está en verde",
        "Moderado: revisar 5×–6× cuando 5,0–10,0 muestra +3%",
        "Agresivo: revisar 20×–25× cuando los intervalos 10–100 concentran actividad",
      ]),
      h2("Gestión del presupuesto"),
      ul([
        "Define un presupuesto estricto por sesión, por ejemplo 50 USD",
        "No intentes recuperar pérdidas",
        "Si usas una progresión tipo martingala, limítala a una sesión que ya hayas decidido detener",
      ]),
      callout(
        "Recordatorio de juego responsable",
        "Aviator funciona con un generador aleatorio. AviatorTrends ayuda a revisar datos, pero no puede garantizar ganancias. Juega con responsabilidad:",
        ["Establece límites de pérdida", "Apuesta solo lo que puedes permitirte", "Trata el juego como entretenimiento"]
      ),
      h2("¿Quieres revisar los datos?"),
      rich("Entra en ", "https://aviatortrends.com", "AviatorTrends", " para consultar el historial y las gráficas en vivo."),
    ],
  },
  "pt-BR": {
    title: "Analise o Aviator da Spribe e os jogos crash com a AviatorTrends",
    description: "Veja como baixar o histórico do Aviator, observar sequências de multiplicadores altos e ler os gráficos da AviatorTrends. O jogo usa RNG e não há ganho garantido.",
    blocks: [
      h1("Analise o Aviator da Spribe e os jogos crash com a AviatorTrends"),
      p("A AviatorTrends organiza o histórico e os gráficos do Aviator para você revisar os dados antes de decidir. Neste guia você aprende a:"),
      ul([
        "Baixar ou analisar o histórico de rodadas do Aviator",
        "Usar o histórico para observar sequências altas",
        "Procurar variação nos gráficos de série temporal",
        "Ler o histograma e a coluna de previsão",
        "Combinar as três ferramentas em uma rotina de análise",
      ]),
      h2("Baixe ou analise o histórico de rodadas"),
      p("Reúna as últimas 300 rodadas ou mais para dar contexto à tendência. Você pode baixar um CSV do Excel na AviatorTrends ou usar os filtros direto no navegador."),
      note("Dica: use 300 pontos ou mais para a distribuição de probabilidade e a previsão terem contexto suficiente."),
      img("spribe-aviator-graphical-analysis", "Amostra de 300 rodadas"),
      h2("1. Histórico de rodadas: onde aparecem as sequências altas"),
      p("O painel de histórico mostra os multiplicadores ao vivo. Procure grupos de resultados de 10× ou mais em vários cassinos num intervalo curto."),
      ul([
        "Filtre por cassino e defina um limite, por exemplo ≥10×, para ver rajadas simultâneas.",
        "Se uma sequência alta aparecer, volte à série e ao histograma antes de mudar o ritmo.",
      ]),
      img("aviator-round-history", "Painel de histórico de rodadas"),
      h2("2. Série temporal: observe a variação"),
      p("A série temporal mostra os multiplicadores das rodadas recentes. Ela ajuda a ver zonas de volatilidade, ciclos e períodos de acúmulo."),
      ul([
        "Amplie 300 rodadas para uma revisão curta.",
        "Use 500 ou mais para procurar ciclos mais longos.",
        "Observe se os picos se repetem a cada 30–60 rodadas.",
      ]),
      p("Neste exemplo, 100 pontos não mostram um padrão claro:"),
      img("aviator-trends-analysis-timeseries-100", "Série temporal do Aviator com 100 pontos"),
      p("Com 300 pontos, o exemplo mostra picos por volta de 13:55, 14:35 e 15:00. O intervalo aproximado é de 30–40 minutos; nesse caso, o seguinte ficava perto de 15:30–15:40."),
      img("aviator-trends-analysis-timeseries-300", "Série temporal do Aviator com 300 pontos"),
      p("Às 15:41, neste exemplo, o multiplicador chegou a 337,28×. Um resultado antigo não determina a próxima rodada."),
      img("spribe-aviator-prediction", "Exemplo de revisão do Aviator da Spribe"),
      h2("3. Histograma: distribuição de probabilidade"),
      p("O histograma conta quantas vezes o multiplicador cai em cada faixa. A coluna de previsão destaca faixas que aparecem menos do que o habitual na amostra."),
      img("forecast", "Distribuição da previsão"),
      p("Verde, previsão positiva, marca faixas menos frequentes na amostra. Vermelho, previsão negativa, marca faixas que acabaram de aparecer. Revise 300 pontos ou mais antes de interpretar."),
      h2("4. Como combinar as três ferramentas"),
      ol([
        "Analise ou baixe o histórico de rodadas",
        "Verifique o histórico em busca de sequências altas",
        "Use a série temporal para ver ciclos",
        "Consulte o histograma para comparar faixas",
        "Registre os resultados e ajuste a revisão",
      ]),
      h2("Exemplos de leitura"),
      ul([
        "Conservador: revisar saques de 1,3×–1,5× quando a faixa está verde",
        "Moderado: revisar 5×–6× quando 5,0–10,0 mostra +3%",
        "Agressivo: revisar 20×–25× quando as faixas 10–100 concentram atividade",
      ]),
      h2("Controle da banca"),
      ul([
        "Defina um orçamento rígido por sessão, por exemplo US$ 50",
        "Não tente recuperar perdas",
        "Se usar uma progressão no estilo martingale, limite-a a uma sessão que você já decidiu encerrar",
      ]),
      callout(
        "Lembrete de jogo responsável",
        "O Aviator funciona com um gerador aleatório. A AviatorTrends ajuda a revisar dados, mas não pode garantir ganhos. Jogue com responsabilidade:",
        ["Defina limites de perda", "Aposte apenas o que pode perder", "Trate o jogo como entretenimento"]
      ),
      h2("Quer revisar os dados?"),
      rich("Acesse a ", "https://aviatortrends.com", "AviatorTrends", " para consultar o histórico e os gráficos ao vivo."),
    ],
  },
  "pt-PT": {
    title: "Analise o Aviator da Spribe e os jogos crash com a AviatorTrends",
    description: "Veja como transferir o histórico do Aviator, observar sequências de multiplicadores altos e ler os gráficos da AviatorTrends. O jogo usa RNG e não há ganho garantido.",
    blocks: [
      h1("Analise o Aviator da Spribe e os jogos crash com a AviatorTrends"),
      p("A AviatorTrends organiza o histórico e os gráficos do Aviator para rever os dados antes de decidir. Neste guia fica a saber como:"),
      ul([
        "Transferir ou analisar o histórico de rondas do Aviator",
        "Usar o histórico para observar sequências altas",
        "Procurar variação nos gráficos de série temporal",
        "Ler o histograma e a coluna de previsão",
        "Combinar as três ferramentas numa rotina de análise",
      ]),
      h2("Transfira ou analise o histórico de rondas"),
      p("Reúna as últimas 300 rondas ou mais para dar contexto à tendência. Pode transferir um CSV do Excel na AviatorTrends ou usar os filtros diretamente no navegador."),
      note("Sugestão: utilize 300 pontos ou mais para a distribuição de probabilidade e a previsão terem contexto suficiente."),
      img("spribe-aviator-graphical-analysis", "Amostra de 300 rondas"),
      h2("1. Histórico de rondas: onde aparecem as sequências altas"),
      p("O painel de histórico mostra os multiplicadores em direto. Procure grupos de resultados de 10× ou mais em vários casinos num intervalo curto."),
      ul([
        "Filtre por casino e defina um limiar, por exemplo ≥10×, para ver rajadas simultâneas.",
        "Se surgir uma sequência alta, volte à série e ao histograma antes de mudar o ritmo.",
      ]),
      img("aviator-round-history", "Painel de histórico de rondas"),
      h2("2. Série temporal: observe a variação"),
      p("A série temporal mostra os multiplicadores das rondas recentes. Ajuda a ver zonas de volatilidade, ciclos e períodos de acumulação."),
      ul([
        "Amplie 300 rondas para uma revisão curta.",
        "Utilize 500 ou mais para procurar ciclos mais longos.",
        "Observe se os picos se repetem a cada 30–60 rondas.",
      ]),
      p("Neste exemplo, 100 pontos não mostram um padrão claro:"),
      img("aviator-trends-analysis-timeseries-100", "Série temporal do Aviator com 100 pontos"),
      p("Com 300 pontos, o exemplo mostra picos por volta das 13:55, 14:35 e 15:00. O intervalo aproximado é de 30–40 minutos; nesse caso, o seguinte ficava perto das 15:30–15:40."),
      img("aviator-trends-analysis-timeseries-300", "Série temporal do Aviator com 300 pontos"),
      p("Às 15:41, neste exemplo, o multiplicador chegou a 337,28×. Um resultado antigo não determina a ronda seguinte."),
      img("spribe-aviator-prediction", "Exemplo de revisão do Aviator da Spribe"),
      h2("3. Histograma: distribuição de probabilidade"),
      p("O histograma conta quantas vezes o multiplicador cai em cada intervalo. A coluna de previsão destaca intervalos que aparecem menos do que o habitual na amostra."),
      img("forecast", "Distribuição da previsão"),
      p("Verde, previsão positiva, marca intervalos menos frequentes na amostra. Vermelho, previsão negativa, marca intervalos que acabaram de aparecer. Reveja 300 pontos ou mais antes de interpretar."),
      h2("4. Como combinar as três ferramentas"),
      ol([
        "Analise ou transfira o histórico de rondas",
        "Verifique o histórico à procura de sequências altas",
        "Utilize a série temporal para ver ciclos",
        "Consulte o histograma para comparar intervalos",
        "Registe os resultados e ajuste a revisão",
      ]),
      h2("Exemplos de leitura"),
      ul([
        "Conservador: rever levantamentos de 1,3×–1,5× quando o intervalo está verde",
        "Moderado: rever 5×–6× quando 5,0–10,0 mostra +3%",
        "Agressivo: rever 20×–25× quando os intervalos 10–100 concentram atividade",
      ]),
      h2("Gestão da banca"),
      ul([
        "Defina um orçamento rigoroso por sessão, por exemplo 50 USD",
        "Não tente recuperar perdas",
        "Se utilizar uma progressão do tipo martingale, limite-a a uma sessão que já tenha decidido terminar",
      ]),
      callout(
        "Lembrete de jogo responsável",
        "O Aviator funciona com um gerador aleatório. A AviatorTrends ajuda a rever dados, mas não pode garantir ganhos. Jogue com responsabilidade:",
        ["Defina limites de perda", "Aposte apenas o que pode perder", "Trate o jogo como entretenimento"]
      ),
      h2("Quer rever os dados?"),
      rich("Aceda à ", "https://aviatortrends.com", "AviatorTrends", " para consultar o histórico e os gráficos em direto."),
    ],
  },
  de: {
    title: "Aviator von Spribe und Crash-Spiele mit AviatorTrends auswerten",
    description: "So laden Sie die Aviator-Rundenhistorie herunter, hohe Multiplikator-Phasen beobachten und die Diagramme von AviatorTrends lesen. Das Spiel nutzt einen Zufallsgenerator; Gewinne sind nicht garantiert.",
    blocks: [
      h1("Aviator von Spribe und Crash-Spiele mit AviatorTrends auswerten"),
      p("AviatorTrends ordnet die Historie und Diagramme von Aviator, damit Sie die Daten vor einer Entscheidung prüfen können. In dieser Anleitung erfahren Sie, wie Sie:"),
      ul([
        "die Aviator-Rundenhistorie herunterladen oder auswerten",
        "im Rundenverlauf Phasen mit hohen Multiplikatoren beobachten",
        "Schwankungen in Zeitreihendiagrammen suchen",
        "Histogramm und Prognosespalte lesen",
        "alle drei Werkzeuge in einem festen Prüfablauf kombinieren",
      ]),
      h2("Rundenhistorie herunterladen oder auswerten"),
      p("Nehmen Sie die letzten 300 Runden oder mehr, damit die Tendenz mehr Kontext hat. Sie können bei AviatorTrends eine Excel-CSV herunterladen oder die Filter direkt im Browser verwenden."),
      note("Hinweis: Verwenden Sie mindestens 300 Datenpunkte, damit Wahrscheinlichkeitsverteilung und Prognose genug Kontext haben."),
      img("spribe-aviator-graphical-analysis", "Beispiel mit 300 Runden"),
      h2("1. Rundenverlauf: hohe Phasen erkennen"),
      p("Das Verlaufspanel zeigt Multiplikatoren live. Achten Sie auf Gruppen von Ergebnissen ab 10× bei mehreren Casinos in einem kurzen Zeitraum."),
      ul([
        "Filtern Sie nach Casino und setzen Sie eine Schwelle, zum Beispiel ≥10×, um gleichzeitige Serien zu sehen.",
        "Wenn eine hohe Phase erscheint, prüfen Sie Zeitreihe und Histogramm erneut, bevor Sie das Tempo ändern.",
      ]),
      img("aviator-round-history", "Ansicht des Rundenverlaufs"),
      h2("2. Zeitreihe: die Schwankung beobachten"),
      p("Die Zeitreihe zeigt die Multiplikatoren der letzten Runden. Damit lassen sich Schwankungsbereiche, Zyklen und Aufbauphasen sichtbar machen."),
      ul([
        "Vergrößern Sie 300 Runden für eine kurze Prüfung.",
        "Nutzen Sie 500 oder mehr, um längere Zyklen zu suchen.",
        "Prüfen Sie, ob hohe Werte alle 30–60 Runden wiederkehren.",
      ]),
      p("In diesem Beispiel zeigen 100 Punkte kein klares Muster:"),
      img("aviator-trends-analysis-timeseries-100", "Aviator-Zeitreihe mit 100 Punkten"),
      p("Mit 300 Punkten zeigt das Beispiel Ausschläge um 13:55, 14:35 und 15:00. Der Abstand beträgt ungefähr 30–40 Minuten; der nächste lag in diesem Beispiel nahe 15:30–15:40."),
      img("aviator-trends-analysis-timeseries-300", "Aviator-Zeitreihe mit 300 Punkten"),
      p("Um 15:41 erreichte der Multiplikator in diesem Beispiel 337,28×. Ein früheres Ergebnis legt die nächste Runde nicht fest."),
      img("spribe-aviator-prediction", "Beispiel einer Aviator-Auswertung von Spribe"),
      h2("3. Histogramm: Wahrscheinlichkeitsverteilung"),
      p("Das Histogramm zählt, wie oft der Multiplikator in jedes Intervall fällt. Die Prognosespalte hebt Intervalle hervor, die in der Stichprobe seltener auftreten als üblich."),
      img("forecast", "Verteilung der Prognose"),
      p("Grün, positive Prognose, markiert in der Stichprobe seltenere Intervalle. Rot, negative Prognose, markiert Intervalle, die gerade aufgetreten sind. Prüfen Sie mindestens 300 Punkte, bevor Sie das lesen."),
      h2("4. Die drei Werkzeuge zusammen nutzen"),
      ol([
        "Rundenhistorie auswerten oder herunterladen",
        "Den Verlauf auf hohe Phasen prüfen",
        "Die Zeitreihe für Zyklen verwenden",
        "Das Histogramm zum Vergleich der Intervalle nutzen",
        "Ergebnisse notieren und die Prüfung anpassen",
      ]),
      h2("Lesebeispiele"),
      ul([
        "Vorsichtig: Ausstiege bei 1,3×–1,5× prüfen, wenn das Intervall grün ist",
        "Mittel: 5×–6× prüfen, wenn 5,0–10,0 bei +3 % liegt",
        "Offensiv: 20×–25× prüfen, wenn die Intervalle 10–100 viel Aktivität zeigen",
      ]),
      h2("Einsatzbudget"),
      ul([
        "Legen Sie ein festes Budget pro Sitzung fest, zum Beispiel 50 USD",
        "Versuchen Sie nicht, Verluste auszugleichen",
        "Wenn Sie eine Martingale-artige Steigerung verwenden, begrenzen Sie sie auf eine Sitzung, die Sie bereits beenden wollten",
      ]),
      callout(
        "Hinweis zum verantwortungsvollen Spielen",
        "Aviator arbeitet mit einem Zufallsgenerator. AviatorTrends hilft beim Auswerten von Daten, kann aber keine Gewinne garantieren. Spielen Sie verantwortungsvoll:",
        ["Setzen Sie Verlustgrenzen", "Setzen Sie nur Geld ein, dessen Verlust Sie tragen können", "Betrachten Sie das Spiel als Unterhaltung"]
      ),
      h2("Daten ansehen?"),
      rich("Öffnen Sie ", "https://aviatortrends.com", "AviatorTrends", ", um Historie und Live-Diagramme zu prüfen."),
    ],
  },
  it: {
    title: "Analizza Aviator di Spribe e i crash game con AviatorTrends",
    description: "Scopri come scaricare la cronologia di Aviator, osservare sequenze di moltiplicatori alti e leggere i grafici di AviatorTrends. Il gioco usa un RNG e non garantisce vincite.",
    blocks: [
      h1("Analizza Aviator di Spribe e i crash game con AviatorTrends"),
      p("AviatorTrends organizza la cronologia e i grafici di Aviator così puoi controllare i dati prima di decidere. In questa guida vedi come:"),
      ul([
        "Scaricare o analizzare la cronologia dei round di Aviator",
        "Usare la cronologia per osservare sequenze alte",
        "Cercare variazioni nei grafici della serie temporale",
        "Leggere l'istogramma e la colonna di previsione",
        "Combinare i tre strumenti in una routine di analisi",
      ]),
      h2("Scarica o analizza la cronologia dei round"),
      p("Raccogli gli ultimi 300 round o più per dare contesto alla tendenza. Puoi scaricare un CSV di Excel da AviatorTrends oppure usare i filtri direttamente nel browser."),
      note("Suggerimento: usa almeno 300 punti perché la distribuzione di probabilità e la previsione abbiano contesto sufficiente."),
      img("spribe-aviator-graphical-analysis", "Esempio di 300 round"),
      h2("1. Cronologia dei round: dove compaiono le sequenze alte"),
      p("Il pannello della cronologia mostra i moltiplicatori in tempo reale. Cerca gruppi di risultati da 10× in su su più casinò in un intervallo breve."),
      ul([
        "Filtra per casinò e imposta una soglia, per esempio ≥10×, per vedere raffiche simultanee.",
        "Se compare una sequenza alta, ricontrolla serie e istogramma prima di cambiare ritmo.",
      ]),
      img("aviator-round-history", "Pannello della cronologia dei round"),
      h2("2. Serie temporale: osserva la variazione"),
      p("La serie temporale mostra i moltiplicatori dei round recenti. Serve a vedere zone di volatilità, cicli e fasi di accumulo."),
      ul([
        "Ingrandisci 300 round per un controllo breve.",
        "Usa 500 o più per cercare cicli più lunghi.",
        "Osserva se i picchi si ripetono ogni 30–60 round.",
      ]),
      p("In questo esempio, 100 punti non mostrano uno schema chiaro:"),
      img("aviator-trends-analysis-timeseries-100", "Serie temporale di Aviator con 100 punti"),
      p("Con 300 punti, l'esempio mostra picchi intorno alle 13:55, 14:35 e 15:00. L'intervallo approssimativo è di 30–40 minuti; in quel caso il successivo cadeva vicino alle 15:30–15:40."),
      img("aviator-trends-analysis-timeseries-300", "Serie temporale di Aviator con 300 punti"),
      p("Alle 15:41, in questo esempio, il moltiplicatore ha raggiunto 337,28×. Un risultato passato non determina il round successivo."),
      img("spribe-aviator-prediction", "Esempio di revisione di Aviator di Spribe"),
      h2("3. Istogramma: distribuzione di probabilità"),
      p("L'istogramma conta quante volte il moltiplicatore cade in ogni intervallo. La colonna di previsione evidenzia gli intervalli che nel campione compaiono meno del solito."),
      img("forecast", "Distribuzione della previsione"),
      p("Il verde, previsione positiva, indica intervalli meno frequenti nel campione. Il rosso, previsione negativa, indica intervalli appena comparsi. Controlla almeno 300 punti prima di interpretarlo."),
      h2("4. Come combinare i tre strumenti"),
      ol([
        "Analizza o scarica la cronologia dei round",
        "Controlla la cronologia per cercare sequenze alte",
        "Usa la serie temporale per vedere i cicli",
        "Consulta l'istogramma per confrontare gli intervalli",
        "Registra i risultati e aggiorna la revisione",
      ]),
      h2("Esempi di lettura"),
      ul([
        "Prudente: controllare incassi a 1,3×–1,5× quando l'intervallo è verde",
        "Intermedio: controllare 5×–6× quando 5,0–10,0 mostra +3%",
        "Aggressivo: controllare 20×–25× quando gli intervalli 10–100 concentrano attività",
      ]),
      h2("Gestione del budget"),
      ul([
        "Definisci un budget rigido per sessione, per esempio 50 USD",
        "Non cercare di recuperare le perdite",
        "Se usi una progressione in stile martingala, limitala a una sessione che hai già deciso di chiudere",
      ]),
      callout(
        "Promemoria sul gioco responsabile",
        "Aviator funziona con un generatore casuale. AviatorTrends aiuta a esaminare i dati, ma non può garantire vincite. Gioca in modo responsabile:",
        ["Imposta limiti di perdita", "Scommetti solo ciò che puoi permetterti di perdere", "Considera il gioco come intrattenimento"]
      ),
      h2("Vuoi esaminare i dati?"),
      rich("Apri ", "https://aviatortrends.com", "AviatorTrends", " per consultare la cronologia e i grafici in tempo reale."),
    ],
  },
};

module.exports = {
  id: "guide",
  paths: PATHS,
  copy,
  hero: "spribe-aviator-graphical-analysis",
  related: ["dashboard", "predict", "history", "models"],
};
